import React from 'react';
import styled from 'styled-components';

const FeedbackContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  /* RTL Support */
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};
`;

const FeedbackHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
`;

const FeedbackTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`;

const AssessmentBadge = styled.span`
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch(props.assessment) {
      case 'positive': return '#d1edff';
      case 'mixed': return '#fff3cd';
      case 'needs_improvement': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch(props.assessment) {
      case 'positive': return '#0c5460';
      case 'mixed': return '#856404';
      case 'needs_improvement': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const EffectivenessScore = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
`;

const ScoreBar = styled.div`
  width: 60px;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => (props.score * 100)}%;
    background: ${props => {
      if (props.score >= 0.8) return '#28a745';
      if (props.score >= 0.6) return '#ffc107';
      return '#dc3545';
    }};
    transition: width 0.3s ease;
  }
`;

const FeedbackSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h5`
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ImprovementsList = styled.ul`
  margin: 0;
  padding-left: ${props => props.isRTL ? '0' : '16px'};
  padding-right: ${props => props.isRTL ? '16px' : '0'};

  li {
    font-size: 13px;
    line-height: 1.4;
    color: #495057;
    margin-bottom: 4px;
    direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
    text-align: ${props => props.isRTL ? 'right' : 'left'};

    /* Enhanced bidirectional text support */
    unicode-bidi: ${props => props.isRTL ? 'plaintext' : 'normal'};

    /* Better handling for mixed Arabic and English text */
    ${props => props.isRTL && `
      bidi-override: isolate;
      white-space: pre-wrap;
    `}

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const QualityMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const MetricItem = styled.div`
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 2px;
`;

const MetricValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => {
    switch(props.value) {
      case 'excellent': return '#28a745';
      case 'good': return '#28a745';
      case 'enhanced': return '#28a745';
      case 'strong': return '#28a745';
      case 'fair': return '#ffc107';
      case 'moderate': return '#ffc107';
      case 'maintained': return '#6c757d';
      case 'poor': return '#dc3545';
      case 'weak': return '#dc3545';
      case 'diminished': return '#dc3545';
      default: return '#495057';
    }
  }};
  text-transform: capitalize;
`;

const SummaryText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #495057;
  background: white;
  padding: 12px;
  border-radius: 6px;
  border-left: ${props => props.isRTL ? 'none' : '3px solid #007bff'};
  border-right: ${props => props.isRTL ? '3px solid #007bff' : 'none'};
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.isRTL ? 'right' : 'left'};

  /* Enhanced bidirectional text support */
  unicode-bidi: ${props => props.isRTL ? 'plaintext' : 'normal'};
  -webkit-rtl-ordering: logical;
  word-wrap: break-word;
  overflow-wrap: break-word;

  /* Better handling for mixed Arabic and English text */
  ${props => props.isRTL && `
    bidi-override: isolate;
    white-space: pre-wrap;
  `}
`;

// Helper function to decode Unicode escape sequences
const decodeUnicodeText = (text) => {
  if (typeof text !== 'string') return text;

  try {
    // Replace Unicode escape sequences with actual characters
    return text.replace(/\\u[\dA-F]{4}/gi, (match) => {
      return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
  } catch (error) {
    return text;
  }
};

// Helper function to detect Arabic text
const containsArabic = (text) => {
  if (typeof text !== 'string') return false;
  // Check for Arabic Unicode range (U+0600-U+06FF)
  return /[\u0600-\u06FF]/.test(text);
};

// Helper function to check if feedback contains Arabic content
const isRTLContent = (feedback) => {
  if (!feedback) return false;

  const checkText = (value) => {
    if (typeof value === 'string') {
      return containsArabic(value);
    } else if (Array.isArray(value)) {
      return value.some(item => checkText(item));
    } else if (typeof value === 'object' && value !== null) {
      return Object.values(value).some(item => checkText(item));
    }
    return false;
  };

  return checkText(feedback);
};

const AdjustmentFeedback = ({ feedback }) => {
  // Handle both string (raw JSON) and object feedback
  let parsedFeedback;

  try {
    if (typeof feedback === 'string') {
      let jsonString = feedback.trim();

      // Try to extract JSON from the string if it's wrapped in other text
      let jsonMatch = jsonString.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonString = jsonMatch[0];
      }

      // Try to clean up common JSON issues
      jsonString = jsonString
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
        .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
        .trim();

      // First decode Unicode sequences, then parse
      const decodedFeedback = decodeUnicodeText(jsonString);
      console.log('Processing feedback string:', {
        original: jsonString.substring(0, 200),
        decoded: decodedFeedback.substring(0, 200),
        length: jsonString.length
      });

      // Try multiple parsing strategies
      let parseSuccessful = false;

      // Strategy 1: Parse decoded feedback
      try {
        parsedFeedback = JSON.parse(decodedFeedback);
        console.log('Successfully parsed decoded feedback JSON:', parsedFeedback);
        parseSuccessful = true;
      } catch (parseError) {
        console.log('Decoded parsing failed:', parseError.message);
      }

      // Strategy 2: Try original string
      if (!parseSuccessful) {
        try {
          parsedFeedback = JSON.parse(jsonString);
          console.log('Successfully parsed original feedback JSON:', parsedFeedback);
          parseSuccessful = true;
        } catch (parseError) {
          console.log('Original parsing failed:', parseError.message);
        }
      }

      // Strategy 3: Try extracting and fixing common JSON issues
      if (!parseSuccessful) {
        try {
          // Look for a more complete JSON pattern and try to fix quotes
          let fixedJson = jsonString
            .replace(/'/g, '"') // Replace single quotes with double quotes
            .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":') // Add quotes to unquoted keys
            .replace(/:\s*([^",{\[\]}\s][^",{\[\]}]*[^",{\[\]}\s])\s*([,}])/g, ':"$1"$2'); // Quote unquoted string values

          parsedFeedback = JSON.parse(fixedJson);
          console.log('Successfully parsed fixed feedback JSON:', parsedFeedback);
          parseSuccessful = true;
        } catch (parseError) {
          console.log('Fixed JSON parsing failed:', parseError.message);
        }
      }

      // No partial extraction fallback - require valid JSON only

      if (!parseSuccessful) {
        throw new Error('All JSON parsing strategies failed');
      }
    } else if (typeof feedback === 'object' && feedback !== null) {
      parsedFeedback = feedback;
    } else {
      throw new Error('Invalid feedback format');
    }
  } catch (error) {
    console.error('JSON parsing failed:', error);
    console.log('Original feedback:', feedback);
    console.log('Feedback type:', typeof feedback);

    // If parsing fails, display as plain text with Unicode decoding
    const displayText = typeof feedback === 'string' ? decodeUnicodeText(feedback) : String(feedback);
    const isRTL = containsArabic(displayText);

    return (
      <FeedbackContainer isRTL={isRTL}>
        <FeedbackHeader>
          <FeedbackTitle>💡 Adjustment Feedback</FeedbackTitle>
        </FeedbackHeader>
        <SummaryText
          isRTL={isRTL}
          style={{
            borderLeft: isRTL ? 'none' : '3px solid #6c757d',
            borderRight: isRTL ? '3px solid #6c757d' : 'none'
          }}
        >
          {displayText}
        </SummaryText>
      </FeedbackContainer>
    );
  }

  const {
    overall_assessment = 'unknown',
    key_improvements = [],
    concerns = [],
    effectiveness_score = 0,
    recommendations = [],
    summary_feedback = '',
    // Legacy support for old structure
    potential_concerns = [],
    analytical_value = {},
    visual_quality = {},
    alternative_approaches = []
  } = parsedFeedback;

  // Detect if content is RTL (Arabic)
  const isRTL = isRTLContent(parsedFeedback);

  return (
    <FeedbackContainer isRTL={isRTL}>
      <FeedbackHeader>
        <FeedbackTitle>💡 Adjustment Feedback</FeedbackTitle>
        <AssessmentBadge assessment={overall_assessment}>
          {overall_assessment.replace('_', ' ')}
        </AssessmentBadge>
        {effectiveness_score > 0 && (
          <ScoreContainer>
            <EffectivenessScore>
              {Math.round(effectiveness_score * 100)}% effective
            </EffectivenessScore>
            <ScoreBar score={effectiveness_score} />
          </ScoreContainer>
        )}
      </FeedbackHeader>

      {summary_feedback && (
        <FeedbackSection>
          <SummaryText isRTL={isRTL}>
            {decodeUnicodeText(summary_feedback)}
          </SummaryText>
        </FeedbackSection>
      )}

      {key_improvements.length > 0 && (
        <FeedbackSection>
          <SectionTitle>✅ Key Improvements</SectionTitle>
          <ImprovementsList isRTL={isRTL}>
            {key_improvements.map((improvement, index) => (
              <li key={index}>{decodeUnicodeText(improvement)}</li>
            ))}
          </ImprovementsList>
        </FeedbackSection>
      )}

      {(analytical_value && Object.keys(analytical_value).length > 0) && (
        <FeedbackSection>
          <SectionTitle>📊 Analytical Value</SectionTitle>
          <QualityMetrics>
            {analytical_value.question_alignment && (
              <MetricItem>
                <MetricLabel>Question Alignment</MetricLabel>
                <MetricValue value={analytical_value.question_alignment}>
                  {analytical_value.question_alignment}
                </MetricValue>
              </MetricItem>
            )}
            {analytical_value.insight_clarity && (
              <MetricItem>
                <MetricLabel>Insight Clarity</MetricLabel>
                <MetricValue value={analytical_value.insight_clarity}>
                  {analytical_value.insight_clarity}
                </MetricValue>
              </MetricItem>
            )}
            {analytical_value.decision_support && (
              <MetricItem>
                <MetricLabel>Decision Support</MetricLabel>
                <MetricValue value={analytical_value.decision_support}>
                  {analytical_value.decision_support}
                </MetricValue>
              </MetricItem>
            )}
          </QualityMetrics>
        </FeedbackSection>
      )}

      {(visual_quality && Object.keys(visual_quality).length > 0) && (
        <FeedbackSection>
          <SectionTitle>🎨 Visual Quality</SectionTitle>
          <QualityMetrics>
            {visual_quality.clarity && (
              <MetricItem>
                <MetricLabel>Clarity</MetricLabel>
                <MetricValue value={visual_quality.clarity}>
                  {visual_quality.clarity}
                </MetricValue>
              </MetricItem>
            )}
            {visual_quality.design && (
              <MetricItem>
                <MetricLabel>Design</MetricLabel>
                <MetricValue value={visual_quality.design}>
                  {visual_quality.design}
                </MetricValue>
              </MetricItem>
            )}
            {visual_quality.accessibility && (
              <MetricItem>
                <MetricLabel>Accessibility</MetricLabel>
                <MetricValue value={visual_quality.accessibility}>
                  {visual_quality.accessibility}
                </MetricValue>
              </MetricItem>
            )}
          </QualityMetrics>
        </FeedbackSection>
      )}

      {(concerns.length > 0 || potential_concerns.length > 0) && (
        <FeedbackSection>
          <SectionTitle>⚠️ Concerns</SectionTitle>
          <ImprovementsList isRTL={isRTL}>
            {(concerns.length > 0 ? concerns : potential_concerns).map((concern, index) => (
              <li key={index} style={{ color: '#856404' }}>{decodeUnicodeText(concern)}</li>
            ))}
          </ImprovementsList>
        </FeedbackSection>
      )}

      {recommendations.length > 0 && (
        <FeedbackSection>
          <SectionTitle>💡 Recommendations</SectionTitle>
          <ImprovementsList isRTL={isRTL}>
            {recommendations.map((recommendation, index) => (
              <li key={index} style={{ color: '#0c5460' }}>{decodeUnicodeText(recommendation)}</li>
            ))}
          </ImprovementsList>
        </FeedbackSection>
      )}

      {alternative_approaches.length > 0 && (
        <FeedbackSection>
          <SectionTitle>🔄 Alternative Approaches</SectionTitle>
          <ImprovementsList isRTL={isRTL}>
            {alternative_approaches.map((approach, index) => (
              <li key={index} style={{ color: '#6c757d' }}>{decodeUnicodeText(approach)}</li>
            ))}
          </ImprovementsList>
        </FeedbackSection>
      )}
    </FeedbackContainer>
  );
};

export default AdjustmentFeedback;